import { useQuery } from '@apollo/client';
import { faBolt, faChartLine, faComments, faMessage, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import PromptRow from './PromptRow.tsx';
import TeaserChatPreview, { PreviewConversation } from './TeaserChatPreview.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Loader from '../../core/components/Loader.tsx';
import { SAD, WAVE } from '../../core/components/worki/Pose.tsx';
import Worki from '../../core/components/worki/Worki.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import { getDateLocalizations } from '../../core/utils/localization.ts';
import { CUSTOMER_AGENTS_PATH } from '../../customer/constants.ts';
import { canManageCustomer } from '../../users/utils.ts';
import { PROMPT_LIBRARY_PATH, WORKI_REQUEST_CALL_URL } from '../constants.ts';
import { PromptCategoryTranslations, resolveFeaturedPrompts } from '../promptLibraryConfig.ts';
import { GET_WORKI_RECAP } from '../queries.ts';
import { renderPromptLibraryText } from '../utils/promptLibrary.ts';

const QuestionIcon = withIcon(faMessage);
const AskIcon = withIcon(faComments);
const SetupIcon = withIcon(faChartLine);
const AutomateIcon = withIcon(faBolt);
const TogetherIcon = withIcon(faUsers);

const EXAMPLE_PROMPT_COUNT = 3;

type FeatureKey = 'askAnything' | 'improveSetup' | 'automate' | 'workTogether';

const FEATURE_ICONS: Record<FeatureKey, ReturnType<typeof withIcon>> = {
    askAnything: AskIcon,
    improveSetup: SetupIcon,
    automate: AutomateIcon,
    workTogether: TogetherIcon,
};

const RETURNING_FEATURES: FeatureKey[] = ['improveSetup', 'automate', 'workTogether'];
const NEW_FEATURES: FeatureKey[] = ['askAnything', 'automate', 'workTogether'];

interface RecapQuestion {
    content: string;
    createdAt: string;
}

interface WorkiRecap {
    questionCount: number;
    lastQuestions: RecapQuestion[];
    recentConversations: PreviewConversation[];
}

const FeatureCards = ({ features }: { features: FeatureKey[] }) => {
    const { t } = useTranslation('chat');
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {features.map((key) => {
                const FeatureIcon = FEATURE_ICONS[key];
                return (
                    <div
                        key={key}
                        className="flex flex-col gap-1.5 rounded-lg border border-solid border-line-weak bg-surface-weak p-3"
                    >
                        <FeatureIcon className="w-4 text-content-brand-default" />
                        <span className="text-sm font-semibold text-content-strong">
                            {t(`teaser.features.${key}.title`)}
                        </span>
                        <span className="text-xs text-content-medium">{t(`teaser.features.${key}.description`)}</span>
                    </div>
                );
            })}
        </div>
    );
};

const Steps = ({ title, steps }: { title: string; steps: string[] }) => (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-brand-weak px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-content-medium">{title}</span>
        <ol className="m-0 grid list-none grid-cols-1 gap-x-4 gap-y-1.5 p-0 sm:grid-cols-3">
            {steps.map((step, index) => (
                <li key={step} className="flex items-start gap-2 text-xs text-content-strong">
                    <span className="flex h-4 w-4 flex-none items-center justify-center rounded-full bg-surface-brand-strong-default text-xs font-semibold text-content-white dark:text-content-black">
                        {index + 1}
                    </span>
                    <span>{step}</span>
                </li>
            ))}
        </ol>
    </div>
);

const SectionTitle = ({ children }: { children: string }) => (
    <span className="text-xs font-semibold uppercase tracking-wide text-content-medium">{children}</span>
);

const LastQuestions = ({ questions }: { questions: RecapQuestion[] }) => {
    const { t } = useTranslation('chat');
    const locale = getDateLocalizations();
    return (
        <div className="flex flex-col gap-2">
            <SectionTitle>{t('teaser.returning.lastQuestions')}</SectionTitle>
            {questions.map((question) => (
                <PromptRow
                    key={`${question.createdAt}-${question.content}`}
                    icon={QuestionIcon}
                    iconClassName="text-content-medium"
                    text={question.content}
                    description={format(new Date(question.createdAt), 'd MMM yyyy', { locale })}
                />
            ))}
        </div>
    );
};

const ExamplePrompts = () => {
    const { t } = useTranslation('chat');
    const categories = t('promptLibrary.categories', { returnObjects: true }) as PromptCategoryTranslations;
    const examples = resolveFeaturedPrompts(categories)
        .filter((prompt) => !prompt.requiresAttachment)
        .slice(0, EXAMPLE_PROMPT_COUNT);
    return (
        <div className="flex flex-col gap-2">
            <SectionTitle>{t('teaser.new.examplesTitle')}</SectionTitle>
            {examples.map(({ promptId, text, label, icon }) => (
                <PromptRow
                    key={promptId}
                    icon={icon}
                    iconClassName="text-content-brand-default"
                    text={renderPromptLibraryText(text)}
                    description={label}
                />
            ))}
            <Link to={PROMPT_LIBRARY_PATH} className="mt-1 text-sm font-medium text-content-brand-default underline">
                {t('teaser.new.browseAll')}
            </Link>
        </div>
    );
};

const RequestCall = () => {
    const { t } = useTranslation('chat');
    return (
        <div className="flex flex-col items-center gap-2">
            <Button variant="primary" className="w-full py-2.5 text-center" asChild>
                <a href={WORKI_REQUEST_CALL_URL} target="_blank" rel="noopener noreferrer">
                    {t('teaser.requestCall')}
                </a>
            </Button>
            <span className="text-center text-xs text-content-weak">{t('teaser.footnote')}</span>
        </div>
    );
};

const ManageSettings = () => {
    const { t } = useTranslation('chat');
    return (
        <Button className="w-full py-2.5 text-center" asChild>
            <Link to={CUSTOMER_AGENTS_PATH}>{t('disabledByAdmin.manageSettings')}</Link>
        </Button>
    );
};

const ReturningView = ({ recap }: { recap: WorkiRecap }) => {
    const { t } = useTranslation('chat');
    return (
        <>
            <h1 className="m-0 text-2xl font-semibold text-content-strong">{t('teaser.returning.title')}</h1>
            <p className="m-0 text-sm text-content-medium">
                {t('teaser.returning.description', { count: recap.questionCount })}
            </p>
            <LastQuestions questions={recap.lastQuestions} />
            <div className="flex flex-col gap-2">
                <SectionTitle>{t('teaser.returning.moreTitle')}</SectionTitle>
                <FeatureCards features={RETURNING_FEATURES} />
            </div>
            <Steps
                title={t('teaser.returning.nextTitle')}
                steps={t('teaser.returning.steps', { returnObjects: true }) as string[]}
            />
        </>
    );
};

const NewUserView = () => {
    const { t } = useTranslation('chat');
    return (
        <>
            <h1 className="m-0 text-2xl font-semibold text-content-strong">{t('teaser.new.title')}</h1>
            <p className="m-0 text-sm text-content-medium">{t('teaser.new.description')}</p>
            <FeatureCards features={NEW_FEATURES} />
            <ExamplePrompts />
            <Steps
                title={t('teaser.new.nextTitle')}
                steps={t('teaser.new.steps', { returnObjects: true }) as string[]}
            />
        </>
    );
};

// what a user without chat access sees in place of Worki: after the adoption trial a recap of their own
// questions, otherwise an introduction; both end in a call request. A customer whose admin switched Worki off
// gets the introduction regardless of past use
export const WorkiTeaserPage = ({ disabledByAdmin = false }: { disabledByAdmin?: boolean }) => {
    const { t } = useTranslation('chat');
    const { user } = useApplicationContext();
    const { data } = useQuery<{ workiRecap: WorkiRecap }>(GET_WORKI_RECAP);
    const recap = data?.workiRecap;
    const returning = !disabledByAdmin && (recap?.questionCount ?? 0) > 0;

    return (
        <TeaserChatPreview conversations={recap?.recentConversations}>
            <div className="my-auto flex h-fit w-full max-w-2xl flex-col gap-4 rounded-2xl border border-solid border-line-weak bg-surface-weak p-6">
                <div className="flex items-center gap-3">
                    <Worki pose={returning ? SAD : WAVE} size={44} trackMouse="none" />
                    {recap && (
                        <span className="rounded-full bg-surface-brand-medium-default px-3 py-1 text-xs font-medium text-content-brand-strong">
                            {returning ? t('teaser.trialEndedTag') : t('teaser.meetTag')}
                        </span>
                    )}
                </div>
                {!recap ? <Loader /> : returning ? <ReturningView recap={recap} /> : <NewUserView />}
                <RequestCall />
                {disabledByAdmin && canManageCustomer(user) && <ManageSettings />}
            </div>
        </TeaserChatPreview>
    );
};

export default WorkiTeaserPage;
